import axios, { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import type {
  CreateKitSerialPairRequest,
  CreateKitSerialPairResponse,
  DeleteKitSerialPairResponse,
  KitSerialPair,
  PaginatedKitSerialPairs,
} from '@/domain/kit-serial-pair';
import type { DispatchSerialCheck } from '@/domain/izzi';
import type {
  ProcessInventoryPayload,
  ProcessInventoryResponse,
  PurchaseOrderItem,
  PurchaseOrderItemsResponse,
} from '@/domain/inventory';
import { checkSerialStatus, isSerialBlocked } from '@/utils/check-serial-status';
import {
  KIT_CONTROL_SKU,
  getReceptionScanMode,
  type ReceptionScanMode,
} from '@/utils/purchase-order-item';
import { VITE_URL_SERVICE } from '@/utils/envs';

export type ScanTab = 0 | 1;
export type WithoutPoMode = 'validate' | 'kit';

const httpPublic = axios.create({
  baseURL: VITE_URL_SERVICE,
  headers: {
    Accept: 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

const cleanMessage = (msg: string) => msg.replace(/^(\[.*?\]\s*)+/, '').trim();

const getPoLoadErrorMessage = (status: number | undefined): string => {
  if (status === 400) return 'Verifica el número de PO';
  if (status === 404) return 'Orden de compra no encontrada';
  if (status === 500) return 'No se pudo cargar la PO, intenta de nuevo';
  return 'No se pudo cargar la PO, intenta de nuevo';
};

const getKitPairErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim()) {
      return cleanMessage(message);
    }
  }
  return 'No se pudo registrar la pareja de kit. Intenta de nuevo.';
};

export const useReception = () => {
  const [poId, setPoId] = useState('');
  const [loadedPoId, setLoadedPoId] = useState('');
  const [sku, setSku] = useState('');
  const [poItems, setPoItems] = useState<PurchaseOrderItem[]>([]);
  const [serialNumber, setSerialNumber] = useState('');
  const [boxSerial, setBoxSerial] = useState('');
  const [controlSerial, setControlSerial] = useState('');
  const [validatedBoxSerial, setValidatedBoxSerial] = useState('');
  const [isLoadingPo, setIsLoadingPo] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const [filterPrincipalSerial, setFilterPrincipalSerial] = useState('');
  const [appliedKitFilter, setAppliedKitFilter] = useState('');
  const [kitPairs, setKitPairs] = useState<KitSerialPair[]>([]);
  const [isLoadingKitPairs, setIsLoadingKitPairs] = useState(false);
  const [kitPaginationModel, setKitPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [kitPairsTotal, setKitPairsTotal] = useState(0);

  const [scanTab, setScanTab] = useState<ScanTab>(0);
  const [withoutPoMode, setWithoutPoMode] = useState<WithoutPoMode>('validate');
  const [withoutPoSerial, setWithoutPoSerial] = useState('');
  const [withoutPoBoxSerial, setWithoutPoBoxSerial] = useState('');
  const [withoutPoControlSerial, setWithoutPoControlSerial] = useState('');
  const [validatedWithoutPoBox, setValidatedWithoutPoBox] = useState('');
  const [isCheckingWithoutPo, setIsCheckingWithoutPo] = useState(false);
  const [withoutPoCheck, setWithoutPoCheck] = useState<DispatchSerialCheck | null>(null);
  const [withoutPoBoxCheck, setWithoutPoBoxCheck] = useState<DispatchSerialCheck | null>(null);
  const [withoutPoControlCheck, setWithoutPoControlCheck] = useState<DispatchSerialCheck | null>(
    null,
  );

  const serialInputRef = useRef<HTMLInputElement>(null);
  const boxInputRef = useRef<HTMLInputElement>(null);
  const controlInputRef = useRef<HTMLInputElement>(null);
  const withoutPoSerialRef = useRef<HTMLInputElement>(null);
  const withoutPoBoxRef = useRef<HTMLInputElement>(null);
  const withoutPoControlRef = useRef<HTMLInputElement>(null);

  const selectedItem = poItems.find((item) => item.sku === sku);
  const scanMode: ReceptionScanMode = getReceptionScanMode(selectedItem);
  const isKitMode = scanMode === 'kit';

  const resetScanFields = useCallback(() => {
    setSerialNumber('');
    setBoxSerial('');
    setControlSerial('');
    setValidatedBoxSerial('');
  }, []);

  const resetPoState = useCallback(() => {
    setLoadedPoId('');
    setSku('');
    setPoItems([]);
    resetScanFields();
  }, [resetScanFields]);

  const resetKitScanState = useCallback(() => {
    setBoxSerial('');
    setControlSerial('');
    setValidatedBoxSerial('');
  }, []);

  const focusInput = (ref: React.RefObject<HTMLInputElement | null>) => {
    setTimeout(() => ref.current?.focus(), 50);
  };

  const fetchPoItems = useCallback(async (purchaseOrder: string) => {
    const { data } = await httpPublic.get<PurchaseOrderItemsResponse>(
      '/api/v3/ops-izzi/purchase-orders/items',
      { params: { purchaseOrder } },
    );
    return data;
  }, []);

  const fetchKitPairs = useCallback(
    async (page: number, pageSize: number, principalSerialFilter: string) => {
      setIsLoadingKitPairs(true);

      try {
        const { data } = await httpPublic.get<PaginatedKitSerialPairs>(
          '/api/v3/ops-izzi/kit-serial-pairs',
          {
            params: {
              page,
              pageSize,
              ...(principalSerialFilter ? { principalSerial: principalSerialFilter } : {}),
            },
          },
        );

        setKitPairs(data.data);
        setKitPairsTotal(data.total);
      } catch {
        toast.error('No se pudo cargar las parejas registradas.');
        setKitPairs([]);
        setKitPairsTotal(0);
      } finally {
        setIsLoadingKitPairs(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchKitPairs(kitPaginationModel.page, kitPaginationModel.pageSize, appliedKitFilter);
  }, [fetchKitPairs, kitPaginationModel, appliedKitFilter]);

  const validateSerialRequest = async (
    payload: ProcessInventoryPayload,
  ): Promise<ProcessInventoryResponse> => {
    const { data } = await httpPublic.post<ProcessInventoryResponse>(
      '/api/v3/ops-izzi/validate-serial',
      payload,
    );
    return data;
  };

  const handleValidationResponse = (data: ProcessInventoryResponse): 'ok' | 'quarantined' => {
    if (data.status === 'REJECTED' || data.status === 'ERROR') {
      const cleanMsg = cleanMessage(data.message || '');
      throw new Error(
        cleanMsg || 'Hay un error con este número de serie, separa la pieza de inventario',
      );
    }

    if (data.status === 'QUARANTINED') {
      toast.warning(cleanMessage(data.message || '') || 'Serie en cuarentena');
      return 'quarantined';
    }

    toast.success('Número de serie procesado exitosamente.');
    return 'ok';
  };

  const handleValidationError = async (error: unknown): Promise<'quarantined' | 'failed'> => {
    if (isAxiosError(error) && error.response?.status === 422) {
      const data = error.response.data as ProcessInventoryResponse;
      if (data?.status === 'QUARANTINED') {
        toast.warning(cleanMessage(data.message || '') || 'Serie en cuarentena');
        return 'quarantined';
      }
    }

    const rawAxiosError = isAxiosError(error) ? error.response?.data?.message : null;
    const axiosError = rawAxiosError ? cleanMessage(String(rawAxiosError)) : null;
    toast.error(
      axiosError ||
        (error instanceof Error ? error.message : 'Error desconocido al procesar el código.'),
    );
    return 'failed';
  };

  const saveKitPair = async (principalSerial: string, complementSerial: string) => {
    const payload: CreateKitSerialPairRequest = {
      principalSerial,
      complementSerial,
    };

    const { data } = await httpPublic.post<CreateKitSerialPairResponse>(
      '/api/v3/ops-izzi/kit-serial-pairs',
      payload,
    );

    toast.success(
      `Kit vinculado: caja ${data.principalSerial} + control ${data.complementSerial}`,
    );
  };

  const refreshKitPairsList = useCallback(() => {
    fetchKitPairs(kitPaginationModel.page, kitPaginationModel.pageSize, appliedKitFilter);
  }, [fetchKitPairs, kitPaginationModel, appliedKitFilter]);

  const onLoadPo = useCallback(async () => {
    const trimmedPo = poId.trim();
    if (!trimmedPo) {
      toast.warning('Ingresa el identificador de PO.');
      return;
    }

    setIsLoadingPo(true);
    resetPoState();
    setPoId(trimmedPo);

    try {
      const data = await fetchPoItems(trimmedPo);
      setLoadedPoId(trimmedPo);
      setPoItems(data.items);

      if (data.items.length === 0) {
        toast.warning('La PO no tiene productos disponibles para escanear.');
        return;
      }

      if (data.items.length === 1) {
        setSku(data.items[0].sku);
        focusInput(serialInputRef);
      }
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      toast.error(getPoLoadErrorMessage(status));
      resetPoState();
      setPoId(trimmedPo);
    } finally {
      setIsLoadingPo(false);
    }
  }, [poId, fetchPoItems, resetPoState]);

  const refreshPoItems = useCallback(async () => {
    if (!loadedPoId) return;

    try {
      const data = await fetchPoItems(loadedPoId);
      setPoItems(data.items);

      if (sku && !data.items.some((item) => item.sku === sku)) {
        setSku(data.items.length === 1 ? data.items[0].sku : '');
        resetKitScanState();
      }
    } catch {
      // Silencioso: el escaneo ya fue procesado; el conteo se actualizará en el próximo load manual
    }
  }, [loadedPoId, sku, fetchPoItems, resetKitScanState]);

  const onProcessSingleScan = async () => {
    if (!loadedPoId || !sku.trim()) {
      toast.warning('Carga la PO y selecciona un SKU antes de escanear.');
      focusInput(serialInputRef);
      return;
    }

    if (!serialNumber.trim()) {
      return;
    }

    setIsValidating(true);

    try {
      const data = await validateSerialRequest({
        purchaseOrder: loadedPoId,
        sku: sku.trim(),
        iccid: serialNumber.trim(),
      });

      handleValidationResponse(data);
      await refreshPoItems();
    } catch (error) {
      const result = await handleValidationError(error);
      if (result === 'quarantined') {
        await refreshPoItems();
      }
    } finally {
      setSerialNumber('');
      setIsValidating(false);
      focusInput(serialInputRef);
    }
  };

  const onProcessKitBoxScan = async () => {
    if (!loadedPoId || !sku.trim()) {
      toast.warning('Carga la PO y selecciona el SKU de caja antes de escanear.');
      focusInput(boxInputRef);
      return;
    }

    const trimmedBoxSerial = boxSerial.trim();
    if (!trimmedBoxSerial) {
      return;
    }

    setIsValidating(true);

    try {
      const data = await validateSerialRequest({
        purchaseOrder: loadedPoId,
        sku: sku.trim(),
        iccid: trimmedBoxSerial,
      });

      const result = handleValidationResponse(data);
      if (result === 'ok') {
        setValidatedBoxSerial(trimmedBoxSerial);
        setBoxSerial('');
        focusInput(controlInputRef);
      } else {
        resetKitScanState();
        await refreshPoItems();
      }
    } catch (error) {
      const result = await handleValidationError(error);
      resetKitScanState();
      if (result === 'quarantined') {
        await refreshPoItems();
      }
    } finally {
      setIsValidating(false);
    }
  };

  const onProcessKitControlScan = async () => {
    if (!validatedBoxSerial) {
      toast.warning('Primero escanea y valida la serie de caja.');
      focusInput(boxInputRef);
      return;
    }

    if (!loadedPoId) {
      toast.warning('Carga la PO antes de escanear.');
      return;
    }

    const trimmedControlSerial = controlSerial.trim();
    if (!trimmedControlSerial) {
      return;
    }

    setIsValidating(true);

    try {
      const data = await validateSerialRequest({
        purchaseOrder: loadedPoId,
        sku: KIT_CONTROL_SKU,
        iccid: trimmedControlSerial,
      });

      const validationResult = handleValidationResponse(data);
      if (validationResult !== 'ok') {
        resetKitScanState();
        await refreshPoItems();
        return;
      }

      try {
        await saveKitPair(validatedBoxSerial, trimmedControlSerial);
        resetKitScanState();
        await refreshPoItems();
        if (kitPaginationModel.page !== 0) {
          setKitPaginationModel((prev) => ({ ...prev, page: 0 }));
        } else {
          refreshKitPairsList();
        }
        focusInput(boxInputRef);
      } catch (pairError) {
        toast.error(getKitPairErrorMessage(pairError));
        resetKitScanState();
      }
    } catch (error) {
      const result = await handleValidationError(error);
      resetKitScanState();
      if (result === 'quarantined') {
        await refreshPoItems();
      }
    } finally {
      setControlSerial('');
      setIsValidating(false);
    }
  };

  const onKeyDownSerialNumber = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onProcessSingleScan();
    }
  };

  const onKeyDownBoxSerial = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onProcessKitBoxScan();
    }
  };

  const onKeyDownControlSerial = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onProcessKitControlScan();
    }
  };

  const onKeyDownPoId = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onLoadPo();
    }
  };

  const onBlurPoId = () => {
    const trimmed = poId.trim();
    if (trimmed && trimmed !== loadedPoId) {
      onLoadPo();
    }
  };

  const onChangePoId = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPoId(value);

    if (loadedPoId && value.trim() !== loadedPoId) {
      resetPoState();
    }
  };

  const onChangeSku = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSku(event.target.value);
    resetScanFields();
  };

  const onChangeSerialNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSerialNumber(event.target.value);
  };

  const onChangeBoxSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBoxSerial(event.target.value);
    if (validatedBoxSerial) {
      setValidatedBoxSerial('');
    }
  };

  const onChangeControlSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setControlSerial(event.target.value);
  };

  const onChangeFilterPrincipalSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterPrincipalSerial(event.target.value);
  };

  const onKeyDownKitFilter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSearchKitPairs();
    }
  };

  const onSearchKitPairs = () => {
    setAppliedKitFilter(filterPrincipalSerial.trim());
    setKitPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onClearKitFilter = () => {
    setFilterPrincipalSerial('');
    setAppliedKitFilter('');
    setKitPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const onChangeKitPaginationModel = (model: { page: number; pageSize: number }) => {
    setKitPaginationModel(model);
  };

  const resetWithoutPoState = useCallback(() => {
    setWithoutPoSerial('');
    setWithoutPoBoxSerial('');
    setWithoutPoControlSerial('');
    setValidatedWithoutPoBox('');
    setWithoutPoCheck(null);
    setWithoutPoBoxCheck(null);
    setWithoutPoControlCheck(null);
  }, []);

  const notifySerialCheck = (check: DispatchSerialCheck) => {
    if (check.outcome === 'QUARANTINED') {
      toast.warning('Serie en cuarentena. No se puede usar.');
      return;
    }
    if (check.outcome === 'REJECTED' || check.outcome === 'ERROR') {
      toast.error('Esta serie no se puede usar.');
      return;
    }
    if (check.outcome === 'RELEASED') {
      toast.success('Serie liberada. Se puede usar.');
      return;
    }
    toast.success('No está en cuarentena. Se puede usar.');
  };

  const onScanTabChange = (_: React.SyntheticEvent, value: ScanTab) => {
    setScanTab(value);
    if (value === 1) {
      setTimeout(() => {
        if (withoutPoMode === 'kit') {
          withoutPoBoxRef.current?.focus();
        } else {
          withoutPoSerialRef.current?.focus();
        }
      }, 50);
    }
  };

  const onWithoutPoModeChange = (_: React.MouseEvent<HTMLElement>, value: WithoutPoMode | null) => {
    if (!value) return;
    setWithoutPoMode(value);
    resetWithoutPoState();
    setTimeout(() => {
      if (value === 'kit') {
        withoutPoBoxRef.current?.focus();
      } else {
        withoutPoSerialRef.current?.focus();
      }
    }, 50);
  };

  const onChangeWithoutPoSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWithoutPoSerial(event.target.value);
    if (withoutPoCheck) {
      setWithoutPoCheck(null);
    }
  };

  const onChangeWithoutPoBoxSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWithoutPoBoxSerial(event.target.value);
    if (validatedWithoutPoBox) {
      setValidatedWithoutPoBox('');
    }
    if (withoutPoBoxCheck) {
      setWithoutPoBoxCheck(null);
    }
  };

  const onChangeWithoutPoControlSerial = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWithoutPoControlSerial(event.target.value);
    if (withoutPoControlCheck) {
      setWithoutPoControlCheck(null);
    }
  };

  const onCheckWithoutPoSerial = async () => {
    const iccid = withoutPoSerial.trim();
    if (!iccid) {
      toast.warning('Escanea o ingresa la serie.');
      return;
    }

    setIsCheckingWithoutPo(true);

    try {
      const check = await checkSerialStatus(iccid);
      setWithoutPoCheck(check);
      notifySerialCheck(check);
    } catch {
      toast.error('No se pudo validar la serie. Intenta de nuevo.');
    } finally {
      setIsCheckingWithoutPo(false);
      focusInput(withoutPoSerialRef);
    }
  };

  const onProcessWithoutPoBox = async () => {
    const iccid = withoutPoBoxSerial.trim();
    if (!iccid) {
      return;
    }

    setIsCheckingWithoutPo(true);

    try {
      const check = await checkSerialStatus(iccid);
      setWithoutPoBoxCheck(check);
      setWithoutPoControlCheck(null);

      if (isSerialBlocked(check)) {
        setValidatedWithoutPoBox('');
        notifySerialCheck(check);
        return;
      }

      setValidatedWithoutPoBox(iccid);
      setWithoutPoBoxSerial('');
      notifySerialCheck(check);
      focusInput(withoutPoControlRef);
    } catch {
      toast.error('No se pudo validar la serie de caja. Intenta de nuevo.');
      setValidatedWithoutPoBox('');
    } finally {
      setIsCheckingWithoutPo(false);
    }
  };

  const onProcessWithoutPoControl = async () => {
    if (!validatedWithoutPoBox) {
      toast.warning('Primero valida la serie de caja.');
      focusInput(withoutPoBoxRef);
      return;
    }

    const iccid = withoutPoControlSerial.trim();
    if (!iccid) {
      return;
    }

    if (iccid === validatedWithoutPoBox) {
      toast.warning('La serie de control no puede ser la misma que la de caja.');
      return;
    }

    setIsCheckingWithoutPo(true);

    try {
      const check = await checkSerialStatus(iccid);
      setWithoutPoControlCheck(check);

      if (isSerialBlocked(check)) {
        notifySerialCheck(check);
        return;
      }

      try {
        await saveKitPair(validatedWithoutPoBox, iccid);
        setWithoutPoBoxSerial('');
        setWithoutPoControlSerial('');
        setValidatedWithoutPoBox('');
        setWithoutPoBoxCheck(null);
        setWithoutPoControlCheck(null);
        if (kitPaginationModel.page !== 0) {
          setKitPaginationModel((prev) => ({ ...prev, page: 0 }));
        } else {
          refreshKitPairsList();
        }
        focusInput(withoutPoBoxRef);
      } catch (error) {
        toast.error(getKitPairErrorMessage(error));
      }
    } catch {
      toast.error('No se pudo validar la serie de control. Intenta de nuevo.');
    } finally {
      setIsCheckingWithoutPo(false);
    }
  };

  const onKeyDownWithoutPoSerial = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onCheckWithoutPoSerial();
    }
  };

  const onKeyDownWithoutPoBox = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onProcessWithoutPoBox();
    }
  };

  const onKeyDownWithoutPoControl = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onProcessWithoutPoControl();
    }
  };

  const onClearWithoutPoCheck = () => {
    resetWithoutPoState();
    setTimeout(() => {
      if (withoutPoMode === 'kit') {
        withoutPoBoxRef.current?.focus();
      } else {
        withoutPoSerialRef.current?.focus();
      }
    }, 50);
  };

  const onDeleteKitPair = async (id: string) => {
    const confirmed = window.confirm('¿Eliminar esta pareja de kit? Esta acción no se puede deshacer.');
    if (!confirmed) return;

    setIsLoadingKitPairs(true);

    try {
      const { data } = await httpPublic.delete<DeleteKitSerialPairResponse>(
        `/api/v3/ops-izzi/kit-serial-pairs/${id}`,
      );

      toast.success(data.message || 'Pareja de kit eliminada correctamente.');
      refreshKitPairsList();
    } catch (error) {
      toast.error(getKitPairErrorMessage(error));
      setIsLoadingKitPairs(false);
    }
  };

  const isPoLoaded = loadedPoId.length > 0 && poItems.length > 0;
  const canScanSingle = isPoLoaded && !!sku && !isLoadingPo && !isValidating;
  const canScanOptionalBox = isPoLoaded && isKitMode && !isLoadingPo && !isValidating;
  const canScanOptionalControl =
    isPoLoaded && isKitMode && !!validatedBoxSerial && !isLoadingPo && !isValidating;

  const canScanWithoutPo =
    withoutPoMode === 'validate' && !isCheckingWithoutPo && !isValidating && !isLoadingPo;
  const canScanWithoutPoBox =
    withoutPoMode === 'kit' && !isCheckingWithoutPo && !isValidating && !isLoadingPo;
  const canScanWithoutPoControl =
    withoutPoMode === 'kit' &&
    !!validatedWithoutPoBox &&
    !isCheckingWithoutPo &&
    !isValidating &&
    !isLoadingPo;

  return {
    poId,
    onChangePoId,
    onKeyDownPoId,
    onBlurPoId,
    onLoadPo,
    sku,
    onChangeSku,
    poItems,
    selectedItem,
    scanMode,
    isKitMode,
    serialNumber,
    onChangeSerialNumber,
    boxSerial,
    onChangeBoxSerial,
    controlSerial,
    onChangeControlSerial,
    validatedBoxSerial,
    isLoadingPo,
    isValidating,
    isPoLoaded,
    canScanSingle,
    canScanOptionalBox,
    canScanOptionalControl,
    serialInputRef,
    boxInputRef,
    controlInputRef,
    onKeyDownSerialNumber,
    onKeyDownBoxSerial,
    onKeyDownControlSerial,
    kitPairs,
    isLoadingKitPairs,
    filterPrincipalSerial,
    appliedKitFilter,
    kitPaginationModel,
    kitPairsTotal,
    onChangeFilterPrincipalSerial,
    onKeyDownKitFilter,
    onSearchKitPairs,
    onClearKitFilter,
    onChangeKitPaginationModel,
    onDeleteKitPair,
    scanTab,
    onScanTabChange,
    withoutPoMode,
    onWithoutPoModeChange,
    withoutPoSerial,
    onChangeWithoutPoSerial,
    onKeyDownWithoutPoSerial,
    onCheckWithoutPoSerial,
    withoutPoBoxSerial,
    onChangeWithoutPoBoxSerial,
    onKeyDownWithoutPoBox,
    withoutPoControlSerial,
    onChangeWithoutPoControlSerial,
    onKeyDownWithoutPoControl,
    validatedWithoutPoBox,
    withoutPoCheck,
    withoutPoBoxCheck,
    withoutPoControlCheck,
    isCheckingWithoutPo,
    onClearWithoutPoCheck,
    withoutPoSerialRef,
    withoutPoBoxRef,
    withoutPoControlRef,
    canScanWithoutPo,
    canScanWithoutPoBox,
    canScanWithoutPoControl,
  };
};
